/**
 * Marketplace public listing.
 *
 * Browsing is open to anyone. Buying requires Legal Pro / Business.
 * Search by title/description/tags, filter by atto_type, sort by recent / popular / price.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Seo, organizationJsonLd } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Store, Search as SearchIcon, Tag, Sparkles, ShoppingBag, ArrowRight,
  Scale, Gavel, ShieldAlert, BookOpen, Mail, XCircle, FileSignature, Loader2,
} from "lucide-react";

const ATTI_ICONS = {
  memoria_difensiva: Scale,
  ricorso_cassazione: Gavel,
  istanza_sospensione: ShieldAlert,
  parere_legale: BookOpen,
  diffida: Mail,
  lettera_recesso: XCircle,
};

const ATTI_LABELS = {
  memoria_difensiva: "Memoria difensiva",
  ricorso_cassazione: "Ricorso per Cassazione",
  istanza_sospensione: "Istanza di sospensione",
  parere_legale: "Parere legale",
  diffida: "Lettera di diffida",
  lettera_recesso: "Lettera di recesso",
};

export default function Marketplace() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [priceTiers, setPriceTiers] = useState([19, 29, 49, 99]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [attoType, setAttoType] = useState("");
  const [sort, setSort] = useState("recent");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 60 };
      if (q.trim()) params.q = q.trim();
      if (attoType) params.atto_type = attoType;
      const r = await api.get("/marketplace/templates", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPriceTiers(r.data.price_tiers || [19, 29, 49, 99]);
    } finally {
      setLoading(false);
    }
  }, [q, attoType, sort]);

  useImperativeLoad(() => { fetchItems(); }, [q, attoType, sort]);

  const lowestPrice = useMemo(() => Math.min(...priceTiers), [priceTiers]);
  const canBuy = user && (user.subscription_tier === "legal_pro" || user.subscription_tier === "business");

  return (
    <div className="min-h-screen bg-[#0A0C1F] text-white" data-testid="marketplace-page">
      <Seo
        title={t("seo.marketplace_title")}
        description={t("seo.marketplace_description")}
        canonicalPath="/marketplace"
        ogImage="/brand/og-marketplace.png"
        jsonLd={organizationJsonLd}
      />
      {/* Hero */}
      <section className="border-b border-slate-800 bg-gradient-to-br from-[#0B0E5C]/40 via-[#0A0C1F] to-[#05073B] px-6 py-16 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/30 bg-[#9ACD32]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#9ACD32]">
            <Store className="h-3 w-3" /> {t("marketplace.pretitle")}
          </span>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="marketplace-title">
            {t("marketplace.title")}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("marketplace.subtitle")}
          </p>
          <p className="mt-2 text-xs text-slate-500">{t("marketplace.public_browse_anyone")}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-wider text-slate-400">
              {t("marketplace.price_from")} <span className="ml-1 font-bold text-[#9ACD32]">€{lowestPrice}</span>
            </span>
            {user && (
              <Link
                to="/legal/atti"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-4 py-2 text-xs font-bold text-[#F4C955] hover:bg-[#D4A937]/20"
                data-testid="marketplace-cta-publish"
              >
                <Sparkles className="h-3.5 w-3.5" /> {t("marketplace.cta_publish")}
              </Link>
            )}
            {!canBuy && user && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-full bg-[#9ACD32] px-4 py-2 text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="marketplace-cta-upgrade"
              >
                {t("marketplace.upgrade_cta_buy")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
        <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("marketplace.search_ph")}
              className="border-slate-700 bg-slate-900 pl-9 text-white"
              data-testid="marketplace-search"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full border-slate-700 bg-slate-900 text-white lg:w-52" data-testid="marketplace-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-white">
              <SelectItem value="recent">{t("marketplace.sort_recent")}</SelectItem>
              <SelectItem value="popular">{t("marketplace.sort_popular")}</SelectItem>
              <SelectItem value="price_asc">{t("marketplace.sort_price_asc")}</SelectItem>
              <SelectItem value="price_desc">{t("marketplace.sort_price_desc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAttoType("")}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
              attoType === "" ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
            }`}
            data-testid="marketplace-filter-all"
          >
            {t("marketplace.filter_all_types")}
          </button>
          {Object.keys(ATTI_LABELS).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setAttoType(id)}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                attoType === id ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
              }`}
              data-testid={`marketplace-filter-${id}`}
            >
              {ATTI_LABELS[id]}
            </button>
          ))}
        </div>

        {loading && (
          <p className="text-center text-sm text-slate-500" data-testid="marketplace-loading">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </p>
        )}

        {!loading && items.length === 0 && (
          <Card className="border-slate-800 bg-slate-900/60 p-12 text-center" data-testid="marketplace-empty">
            <ShoppingBag className="mx-auto h-10 w-10 text-slate-600" />
            <h3 className="mt-3 font-display text-lg font-bold text-white">{t("marketplace.empty_title")}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">{t("marketplace.empty_body")}</p>
            {user && (
              <Link to="/legal/atti" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4A937] px-5 py-2 text-sm font-bold text-[#05073B] hover:bg-[#E0B850]">
                {t("marketplace.cta_publish")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </Card>
        )}

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="marketplace-grid">
            {items.map((tpl) => {
              const Icon = ATTI_ICONS[tpl.atto_type] || FileSignature;
              return (
                <Link
                  key={tpl.template_id}
                  to={tpl.slug ? `/marketplace/t/${tpl.slug}` : `/marketplace/template/${tpl.template_id}`}
                  className="block"
                  data-testid={`marketplace-card-${tpl.template_id}`}
                >
                  <Card className="h-full border-slate-800 bg-slate-900/60 p-5 transition-all hover:-translate-y-1 hover:border-[#D4A937]/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4A937]/15 text-[#F4C955]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="rounded-full bg-[#9ACD32] px-2.5 py-0.5 font-mono text-xs font-extrabold text-[#05073B]">
                        €{tpl.price_eur}
                      </span>
                    </div>
                    <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">
                      {tpl.label}
                    </p>
                    <h3 className="mt-1 line-clamp-2 font-display text-base font-bold text-white">
                      {tpl.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm text-slate-400">
                      {tpl.description}
                    </p>
                    {tpl.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {tpl.tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
                            <Tag className="h-2.5 w-2.5" /> {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500">
                      <span>{t("marketplace.card_by")} {tpl.author_display_name}</span>
                      <span>{t("marketplace.card_purchases").replace("{count}", tpl.purchases_count || 0)}</span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {total > 0 && (
          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="marketplace-count">
            {total} {total === 1 ? "template" : "templates"}
          </p>
        )}
      </section>
    </div>
  );
}
